import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';

const TakumiCertifications = () => {
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCert, setSelectedCert] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [actionMessage, setActionMessage] = useState({ text: '', type: '' });

  // Modal edit form state
  const [editStatus, setEditStatus] = useState('');
  const [editRemarks, setEditRemarks] = useState('');
  const [editCertUrl, setEditCertUrl] = useState('');

  const itemsPerPage = 10;

  const fetchCertifications = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/api/takumi/certification/all`);
      if (!response.ok) {
        throw new Error(`Failed to fetch certifications (HTTP ${response.status})`);
      }

      const result = await response.json();
      if (result.success) {
        setCertifications(result.data || []);
      } else {
        setError(result.message || 'Failed to retrieve certification records');
      }
    } catch (err) {
      console.error('Error fetching certifications:', err);
      setError(err.message || 'Failed to connect to certification server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCertifications();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'under_review':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'approved':
        return 'bg-emerald-100 text-emerald-800 border-emerald-300';
      case 'issued':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'rejected':
        return 'bg-rose-100 text-rose-800 border-rose-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusCount = (status) => {
    if (status === 'all') return certifications.length;
    return certifications.filter((c) => c.status === status).length;
  };

  // Open details / edit modal
  const openModal = (cert) => {
    setSelectedCert(cert);
    setEditStatus(cert.status || 'pending');
    setEditRemarks(cert.adminRemarks || '');
    setEditCertUrl(cert.issuedCertificateUrl || '');
    setActionMessage({ text: '', type: '' });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedCert(null);
    setActionMessage({ text: '', type: '' });
  };

  // Update certification status and details
  const handleUpdateCert = async (e) => {
    e.preventDefault();
    if (!selectedCert) return;

    try {
      setUpdating(true);
      setActionMessage({ text: '', type: '' });

      const targetId = selectedCert._id || selectedCert.certificateId;
      const response = await fetch(`${API_BASE_URL}/api/takumi/certification/${targetId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: editStatus,
          adminRemarks: editRemarks,
          issuedCertificateUrl: editCertUrl
        })
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to update certification');
      }

      setActionMessage({ text: 'Certification updated successfully!', type: 'success' });

      // Update in local state
      setCertifications((prev) =>
        prev.map((c) =>
          (c._id === selectedCert._id || c.certificateId === selectedCert.certificateId)
            ? { ...c, status: editStatus, adminRemarks: editRemarks, issuedCertificateUrl: editCertUrl, issuedAt: editStatus === 'issued' ? (c.issuedAt || new Date()) : c.issuedAt }
            : c
        )
      );

      // Update selected modal object
      setSelectedCert((prev) => ({
        ...prev,
        status: editStatus,
        adminRemarks: editRemarks,
        issuedCertificateUrl: editCertUrl,
        issuedAt: editStatus === 'issued' ? (prev?.issuedAt || new Date()) : prev?.issuedAt
      }));
    } catch (err) {
      console.error('Error updating certification:', err);
      setActionMessage({ text: err.message || 'Failed to update certification', type: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  // Delete certification request
  const handleDeleteCert = async (certId) => {
    if (!window.confirm('Are you sure you want to delete this certification record? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/takumi/certification/${certId}`, {
        method: 'DELETE'
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to delete certification');
      }

      setCertifications((prev) => prev.filter((c) => c._id !== certId && c.certificateId !== certId));
      if (selectedCert && (selectedCert._id === certId || selectedCert.certificateId === certId)) {
        closeModal();
      }
    } catch (err) {
      alert(err.message || 'Error deleting certification record');
    }
  };

  // Filtered certifications
  const filtered = certifications.filter((cert) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      (cert.fullName && cert.fullName.toLowerCase().includes(term)) ||
      (cert.email && cert.email.toLowerCase().includes(term)) ||
      (cert.phone && cert.phone.includes(term)) ||
      (cert.certificateId && cert.certificateId.toLowerCase().includes(term)) ||
      (cert.institution && cert.institution.toLowerCase().includes(term));

    const matchesStatus = statusFilter === 'all' || cert.status === statusFilter;
    const matchesPlan = planFilter === 'all' || cert.plan === planFilter;

    return matchesSearch && matchesStatus && matchesPlan;
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filtered.slice(startIndex, startIndex + itemsPerPage);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-8 px-4">
        <div className="flex flex-col justify-center items-center h-64 space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          <p className="text-gray-500 font-medium text-sm">Loading Takumi Certification applications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <div>
          <div className="flex items-center gap-3">
            <span className="text-3xl">🎓</span>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Takumi Certifications</h1>
              <p className="text-sm text-gray-500 mt-1">
                Review milestone logs, track certification applications, and issue student certificates.
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchCertifications}
            className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium shadow-sm transition-all"
          >
            <span>🔄</span> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-xl flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span>⚠️</span>
            <p className="text-sm font-medium">{error}</p>
          </div>
          <button
            onClick={fetchCertifications}
            className="text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      )}

      {/* KPI Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div
          onClick={() => setStatusFilter('all')}
          className={`cursor-pointer p-4 rounded-xl border transition-all ${
            statusFilter === 'all'
              ? 'bg-gray-900 text-white border-gray-900 shadow-md scale-[1.02]'
              : 'bg-white text-gray-800 border-gray-200 hover:border-gray-400'
          }`}
        >
          <div className="text-xs font-semibold uppercase tracking-wider opacity-75">All Requests</div>
          <div className="text-2xl font-bold mt-1">{getStatusCount('all')}</div>
        </div>

        <div
          onClick={() => setStatusFilter('pending')}
          className={`cursor-pointer p-4 rounded-xl border transition-all ${
            statusFilter === 'pending'
              ? 'bg-amber-500 text-white border-amber-500 shadow-md scale-[1.02]'
              : 'bg-white text-gray-800 border-amber-200 hover:border-amber-400'
          }`}
        >
          <div className="text-xs font-semibold uppercase tracking-wider text-amber-600 opacity-90">Pending</div>
          <div className="text-2xl font-bold mt-1 text-amber-900">{getStatusCount('pending')}</div>
        </div>

        <div
          onClick={() => setStatusFilter('under_review')}
          className={`cursor-pointer p-4 rounded-xl border transition-all ${
            statusFilter === 'under_review'
              ? 'bg-blue-600 text-white border-blue-600 shadow-md scale-[1.02]'
              : 'bg-white text-gray-800 border-blue-200 hover:border-blue-400'
          }`}
        >
          <div className="text-xs font-semibold uppercase tracking-wider text-blue-600 opacity-90">Under Review</div>
          <div className="text-2xl font-bold mt-1 text-blue-900">{getStatusCount('under_review')}</div>
        </div>

        <div
          onClick={() => setStatusFilter('approved')}
          className={`cursor-pointer p-4 rounded-xl border transition-all ${
            statusFilter === 'approved'
              ? 'bg-emerald-600 text-white border-emerald-600 shadow-md scale-[1.02]'
              : 'bg-white text-gray-800 border-emerald-200 hover:border-emerald-400'
          }`}
        >
          <div className="text-xs font-semibold uppercase tracking-wider text-emerald-600 opacity-90">Approved</div>
          <div className="text-2xl font-bold mt-1 text-emerald-900">{getStatusCount('approved')}</div>
        </div>

        <div
          onClick={() => setStatusFilter('issued')}
          className={`cursor-pointer p-4 rounded-xl border transition-all ${
            statusFilter === 'issued'
              ? 'bg-purple-600 text-white border-purple-600 shadow-md scale-[1.02]'
              : 'bg-white text-gray-800 border-purple-200 hover:border-purple-400'
          }`}
        >
          <div className="text-xs font-semibold uppercase tracking-wider text-purple-600 opacity-90">Issued</div>
          <div className="text-2xl font-bold mt-1 text-purple-900">{getStatusCount('issued')}</div>
        </div>

        <div
          onClick={() => setStatusFilter('rejected')}
          className={`cursor-pointer p-4 rounded-xl border transition-all ${
            statusFilter === 'rejected'
              ? 'bg-rose-600 text-white border-rose-600 shadow-md scale-[1.02]'
              : 'bg-white text-gray-800 border-rose-200 hover:border-rose-400'
          }`}
        >
          <div className="text-xs font-semibold uppercase tracking-wider text-rose-600 opacity-90">Rejected</div>
          <div className="text-2xl font-bold mt-1 text-rose-900">{getStatusCount('rejected')}</div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search by student name, email, phone, certificate ID, institution..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
          />
          <span className="absolute left-3 top-2.5 text-gray-400">🔍</span>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600 text-xs font-bold"
            >
              ✕
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="under_review">Under Review</option>
            <option value="approved">Approved</option>
            <option value="issued">Issued</option>
            <option value="rejected">Rejected</option>
          </select>

          <select
            value={planFilter}
            onChange={(e) => {
              setPlanFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
          >
            <option value="all">All Plans</option>
            <option value="edge">Takumi Edge</option>
            <option value="core">Takumi Core</option>
            <option value="pro">Takumi Pro</option>
            <option value="premium">Takumi Premium</option>
          </select>

          {(searchTerm || statusFilter !== 'all' || planFilter !== 'all') && (
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setPlanFilter('all');
                setCurrentPage(1);
              }}
              className="text-xs text-indigo-600 hover:text-indigo-800 font-medium px-2 py-2 underline"
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>

      {/* Certifications Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Tracking ID / Applied
                </th>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Candidate Info
                </th>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Plan / Track
                </th>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Milestones & Progress
                </th>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    <div className="text-3xl mb-2">📄</div>
                    <p className="text-base font-medium text-gray-700">No certification requests found</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Try adjusting your search criteria or filter options.
                    </p>
                  </td>
                </tr>
              ) : (
                currentItems.map((cert) => {
                  const snapshot = cert.progressSnapshot || {};
                  return (
                    <tr key={cert._id || cert.certificateId} className="hover:bg-gray-50/80 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5 font-mono text-xs font-bold text-indigo-700 bg-indigo-50 px-2 py-1 rounded inline-block">
                          {cert.certificateId}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {formatDate(cert.createdAt)}
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <div className="text-sm font-semibold text-gray-900">{cert.fullName}</div>
                        <div className="text-xs text-gray-500 flex flex-col mt-0.5">
                          <span>✉️ {cert.email}</span>
                          <span>📞 {cert.phone}</span>
                          {cert.institution && (
                            <span className="text-gray-400 text-xs italic">🏫 {cert.institution}</span>
                          )}
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800">
                          {cert.planName || (cert.plan === 'edge' ? 'Takumi Edge' : 'Takumi Core')}
                        </span>
                        {cert.specializations && cert.specializations.length > 0 && (
                          <div className="text-xs text-gray-500 mt-1">
                            {cert.specializations.slice(0, 2).join(', ')}
                            {cert.specializations.length > 2 ? ` +${cert.specializations.length - 2}` : ''}
                          </div>
                        )}
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-16 bg-gray-200 rounded-full h-2 overflow-hidden">
                            <div
                              className="bg-emerald-500 h-2 rounded-full"
                              style={{ width: `${Math.min(100, Math.max(0, snapshot.overallProgressPercent || 0))}%` }}
                            ></div>
                          </div>
                          <span className="text-xs font-bold text-gray-700">
                            {snapshot.overallProgressPercent || 0}%
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Milestones: <strong className="text-gray-700">{snapshot.totalMilestonesSolved || 0}</strong> | Mock: <strong className="text-gray-700">{snapshot.mockTestsCompleted || 0}</strong>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusBadge(
                            cert.status
                          )}`}
                        >
                          {cert.status?.replace('_', ' ').toUpperCase() || 'PENDING'}
                        </span>
                        {cert.issuedCertificateUrl && (
                          <a
                            href={cert.issuedCertificateUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-xs text-indigo-600 hover:underline mt-1"
                          >
                            🔗 View Certificate
                          </a>
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openModal(cert)}
                            className="text-xs bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-semibold px-3 py-1.5 rounded-lg border border-indigo-200 transition-colors"
                          >
                            Inspect & Review
                          </button>
                          <button
                            onClick={() => handleDeleteCert(cert._id || cert.certificateId)}
                            className="text-xs text-red-500 hover:text-red-700 p-1.5 hover:bg-red-50 rounded"
                            title="Delete Record"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {filtered.length > itemsPerPage && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <div className="text-xs text-gray-500">
              Showing <span className="font-semibold">{startIndex + 1}</span> to{' '}
              <span className="font-semibold">{Math.min(startIndex + itemsPerPage, filtered.length)}</span> of{' '}
              <span className="font-semibold">{filtered.length}</span> entries
            </div>
            <div className="flex gap-2">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1 text-xs border border-gray-300 rounded-md bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-xs font-semibold text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                className="px-3 py-1 text-xs border border-gray-300 rounded-md bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Review & Issue Certificate Modal */}
      {showModal && selectedCert && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto shadow-2xl border border-gray-200 animate-fadeIn">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-gray-50 to-indigo-50/40 rounded-t-2xl">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🎓</span>
                  <h2 className="text-xl font-bold text-gray-900">Certificate Application Review</h2>
                </div>
                <p className="text-xs font-mono text-indigo-700 mt-1">ID: {selectedCert.certificateId}</p>
              </div>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 text-lg leading-none"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Notification Message */}
              {actionMessage.text && (
                <div
                  className={`p-3 rounded-lg text-sm font-medium ${
                    actionMessage.type === 'success'
                      ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                      : 'bg-rose-50 text-rose-800 border border-rose-200'
                  }`}
                >
                  {actionMessage.text}
                </div>
              )}

              {/* Student Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-xl border border-gray-200">
                <div>
                  <div className="text-xs text-gray-500 uppercase font-semibold">Candidate Name</div>
                  <div className="text-base font-bold text-gray-900">{selectedCert.fullName}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase font-semibold">Registered Email</div>
                  <div className="text-sm font-medium text-gray-900">{selectedCert.email}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase font-semibold">Phone Number</div>
                  <div className="text-sm font-medium text-gray-900">{selectedCert.phone}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase font-semibold">Institution / University</div>
                  <div className="text-sm font-medium text-gray-900">{selectedCert.institution || 'Not specified'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase font-semibold">Enrolled Plan</div>
                  <div className="text-sm font-bold text-indigo-600">
                    {selectedCert.planName || selectedCert.plan?.toUpperCase()}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase font-semibold">Application Date</div>
                  <div className="text-sm text-gray-700">{formatDate(selectedCert.createdAt)}</div>
                </div>
              </div>

              {/* External Links & Profiles */}
              {(selectedCert.linkedinUrl || selectedCert.githubUrl) && (
                <div className="flex flex-wrap gap-3">
                  {selectedCert.linkedinUrl && (
                    <a
                      href={selectedCert.linkedinUrl.startsWith('http') ? selectedCert.linkedinUrl : `https://${selectedCert.linkedinUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
                    >
                      🔗 LinkedIn Profile ↗
                    </a>
                  )}
                  {selectedCert.githubUrl && (
                    <a
                      href={selectedCert.githubUrl.startsWith('http') ? selectedCert.githubUrl : `https://${selectedCert.githubUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-800 border border-gray-300 hover:bg-gray-200"
                    >
                      💻 GitHub Profile ↗
                    </a>
                  )}
                </div>
              )}

              {/* Specializations & Candidate Note */}
              {selectedCert.specializations && selectedCert.specializations.length > 0 && (
                <div>
                  <div className="text-xs text-gray-500 uppercase font-semibold mb-1.5">Specializations Claimed</div>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedCert.specializations.map((spec, idx) => (
                      <span
                        key={idx}
                        className="bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs px-2.5 py-0.5 rounded-full font-medium"
                      >
                        {spec}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {selectedCert.candidateNote && (
                <div className="bg-amber-50/60 border border-amber-200 p-3.5 rounded-xl">
                  <div className="text-xs font-semibold text-amber-800 uppercase mb-1">Candidate Note</div>
                  <p className="text-sm text-gray-700 italic">"{selectedCert.candidateNote}"</p>
                </div>
              )}

              {/* Progress Snapshot Verification Card */}
              <div className="bg-slate-50 border border-slate-200 p-4 rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    System Milestone Snapshot
                  </div>
                  <span className="text-xs font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">
                    {selectedCert.progressSnapshot?.overallProgressPercent || 0}% Completed
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                  <div className="bg-white p-3 rounded-lg border border-slate-200">
                    <div className="text-xs text-gray-500">Milestones Solved</div>
                    <div className="text-lg font-bold text-gray-900">
                      {selectedCert.progressSnapshot?.totalMilestonesSolved || 0}
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-slate-200">
                    <div className="text-xs text-gray-500">Mock Tests Taken</div>
                    <div className="text-lg font-bold text-gray-900">
                      {selectedCert.progressSnapshot?.mockTestsCompleted || 0}
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-slate-200">
                    <div className="text-xs text-gray-500">Average Score</div>
                    <div className="text-lg font-bold text-gray-900">
                      {selectedCert.progressSnapshot?.mockTestsAverageScore || 0}%
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-slate-200">
                    <div className="text-xs text-gray-500">Weeks Finished</div>
                    <div className="text-lg font-bold text-gray-900">
                      {selectedCert.progressSnapshot?.roadmapWeeksCompleted || 0}
                    </div>
                  </div>
                </div>

                {selectedCert.progressSnapshot?.summaryText && (
                  <p className="text-xs text-slate-600 mt-2 italic">
                    Summary: {selectedCert.progressSnapshot.summaryText}
                  </p>
                )}
              </div>

              {/* Admin Action Form */}
              <form onSubmit={handleUpdateCert} className="space-y-4 pt-2 border-t border-gray-200">
                <div className="text-sm font-bold text-gray-900">Admin Actions & Issuance</div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                      Certification Status
                    </label>
                    <select
                      value={editStatus}
                      onChange={(e) => setEditStatus(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                    >
                      <option value="pending">Pending</option>
                      <option value="under_review">Under Review</option>
                      <option value="approved">Approved</option>
                      <option value="issued">Issued (Completed)</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                      Certificate Download / Verification URL
                    </label>
                    <input
                      type="url"
                      placeholder="https://drive.google.com/... or hosted PDF link"
                      value={editCertUrl}
                      onChange={(e) => setEditCertUrl(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                    Admin Remarks / Feedback (Sent or Logged)
                  </label>
                  <textarea
                    rows="3"
                    placeholder="Enter review remarks, milestone verification notes, or reason for status update..."
                    value={editRemarks}
                    onChange={(e) => setEditRemarks(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                  ></textarea>
                </div>

                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Close
                  </button>
                  <button
                    type="submit"
                    disabled={updating}
                    className="px-5 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm transition-colors disabled:opacity-50"
                  >
                    {updating ? 'Saving Changes...' : 'Save & Update Status'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TakumiCertifications;
